import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { TrendingUp, Eye, MousePointer, DollarSign, Clock, X } from 'lucide-react';
import { formatTimeAgo } from '../../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BoostStatsDialog({ post, open, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    if (open && post) {
      loadStats();
    }
  }, [open, post]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/social/posts/${post.id}/boost-stats`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setStats(data);
      } else {
        toast.error('Failed to load boost stats');
      }
    } catch (error) {
      toast.error('Failed to load boost stats');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBoost = async () => {
    if (!window.confirm('Cancel boost and get a prorated refund?')) return;

    try {
      setCanceling(true);
      const res = await fetch(`${API_URL}/api/social/boosts/${stats.boost_id}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(data.message || 'Boost cancelled and refunded');
        onClose();
      } else {
        toast.error(data.detail || 'Failed to cancel boost');
      }
    } catch (error) {
      toast.error('Failed to cancel boost');
    } finally {
      setCanceling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            Boost Performance
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading stats...</div>
        ) : !stats?.boosted ? (
          <div className="text-center py-8 text-gray-500">
            This post is not currently boosted
          </div>
        ) : (
          <div className="space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Status</span>
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Active
              </Badge>
            </div>

            {/* Time Remaining */}
            <Card className="bg-blue-50 dark:bg-blue-900/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                    Time Remaining
                  </span>
                </div>
                <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                  {formatTimeAgo(stats.expires_at)} left
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  Started {formatTimeAgo(stats.started_at)}
                </p>
              </CardContent>
            </Card>

            {/* Performance Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="w-4 h-4 text-gray-600" />
                    <span className="text-xs text-gray-600">Impressions</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.impressions.toLocaleString()}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <MousePointer className="w-4 h-4 text-gray-600" />
                    <span className="text-xs text-gray-600">Clicks</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.clicks.toLocaleString()}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-gray-600" />
                    <span className="text-xs text-gray-600">Tips Received</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.tips_received}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.tips_amount.toFixed(2)} MTR
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-gray-600" />
                    <span className="text-xs text-gray-600">Package</span>
                  </div>
                  <p className="text-lg font-bold capitalize">{stats.package_size}</p>
                </CardContent>
              </Card>
            </div>

            {/* Cancel Button */}
            <Button
              variant="outline"
              onClick={handleCancelBoost}
              disabled={canceling}
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {canceling ? (
                'Cancelling...'
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Cancel Boost & Get Refund
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
